/**
 * PersistentUniver — keeps the Sheets (Univer Workspace) iframe mounted across navigation.
 *
 * Same pattern as PersistentLobeChat / PersistentChatwoot (Plan 1966):
 * - Mounted lazily on the first /sheets visit
 * - Kept mounted (hidden) when navigating away, so unsaved cell edits and scroll survive
 * - Remounted on logout (mountKey bump) so the next user mints their own SSO token
 */

import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import UniverEmbed from "@/components/UniverEmbed/UniverEmbed";
import type { UniverEmbedRef } from "@/components/UniverEmbed/UniverEmbed";
import { useAuthStore } from "@/lib/store/authStore";

const SHEETS_PATH = "/sheets";

export function PersistentUniver() {
  const location = useLocation();
  const isOnSheetsPage = location.pathname.startsWith(SHEETS_PATH);

  const [hasVisitedSheets, setHasVisitedSheets] = useState(false);
  const [mountKey, setMountKey] = useState(0);
  const univerRef = useRef<UniverEmbedRef>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isProvisioned = useAuthStore((state) => state.isProvisioned);
  const currentTenant = useAuthStore((state) => state.currentTenant);

  // Reset on logout
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setHasVisitedSheets(false);
      setMountKey((prev) => prev + 1);
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Lazy mount on first /sheets visit
  useEffect(() => {
    if (isOnSheetsPage && !hasVisitedSheets) {
      setHasVisitedSheets(true);
    }
  }, [isOnSheetsPage, hasVisitedSheets]);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentUniver] Error:", message);
  }, []);

  if (!hasVisitedSheets || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 pb-14 md:pb-0 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnSheetsPage ? "visible" : "hidden",
        pointerEvents: isOnSheetsPage ? "auto" : "none",
      }}
    >
      {/* key includes the tenant: switching store must never keep the previous store's workspace */}
      <UniverEmbed
        ref={univerRef}
        key={`${mountKey}:${currentTenant ?? ""}`}
        className="h-full w-full"
        onError={handleError}
      />
    </div>
  );
}
